import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Product, ProductCategory } from '../models/product.model';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';

const API_URL = environment.apiUrl;

@Injectable({ providedIn: 'root' })
export class ProductService {
  constructor(private http: HttpClient) {}

  getProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(`${API_URL}/api/bakery/products`).pipe(
      map(products => products.map(p => this.normalize(p)))
    );
  }

  getProductById(id: string): Observable<Product | undefined> {
    return this.http.get<Product>(`${API_URL}/api/bakery/products/${id}`).pipe(
      map(p => this.normalize(p))
    );
  }

  getProductsByCategory(category: ProductCategory): Observable<Product[]> {
    return this.http.get<Product[]>(`${API_URL}/api/bakery/products?category=${category}`).pipe(
      map(products => products.map(p => this.normalize(p)))
    );
  }

  getProductsPaginated(limit: number, offset: number): Observable<{ products: Product[]; hasMore: boolean }> {
    return this.getProducts().pipe(
      map(all => {
        const slice = all.slice(offset, offset + limit);
        return { products: slice, hasMore: offset + slice.length < all.length };
      })
    );
  }

  getProductsByCategoryPaginated(category: ProductCategory, limit: number, offset: number): Observable<{ products: Product[]; hasMore: boolean }> {
    return this.getProductsByCategory(category).pipe(
      map(all => {
        const slice = all.slice(offset, offset + limit);
        return { products: slice, hasMore: offset + slice.length < all.length };
      })
    );
  }

  getSimilarProducts(excludeId: string, category: ProductCategory, limit = 6): Observable<Product[]> {
    return this.getProductsByCategory(category).pipe(
      map(products => products.filter(p => String(p.id) !== String(excludeId)).slice(0, limit))
    );
  }

  // ── Admin methods ──
  getAllProductsAdmin(headers: HttpHeaders): Observable<Product[]> {
    return this.http.get<Product[]>(`${API_URL}/api/bakery/products/all`, { headers });
  }

  createProduct(product: Partial<Product>, headers: HttpHeaders): Observable<Product> {
    return this.http.post<Product>(`${API_URL}/api/bakery/products`, product, { headers });
  }

  updateProduct(id: string, product: Partial<Product>, headers: HttpHeaders): Observable<Product> {
    return this.http.put<Product>(`${API_URL}/api/bakery/products/${id}`, product, { headers });
  }

  deleteProduct(id: string, headers: HttpHeaders): Observable<any> {
    return this.http.delete(`${API_URL}/api/bakery/products/${id}`, { headers });
  }

  uploadImage(file: File, headers: HttpHeaders): Observable<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('image', file);
    // Don't set Content-Type — let browser set multipart/form-data boundary
    const uploadHeaders = new HttpHeaders({ Authorization: headers.get('Authorization') || '' });
    return this.http.post<{ imageUrl: string }>(`${API_URL}/api/bakery/upload`, formData, { headers: uploadHeaders });
  }

  /** Normalize DB row to match the Product interface (id as string) and parse JSON */
  private normalize(p: any): Product {
    const raw = { ...p, id: String(p.id) };
    
    // Safely parse JSON strings to arrays/objects if backend returns strings
    if (typeof raw.ingredients === 'string') {
      try { raw.ingredients = JSON.parse(raw.ingredients); } catch { raw.ingredients = []; }
    }
    if (typeof raw.images === 'string') {
      try { raw.images = JSON.parse(raw.images); } catch { raw.images = []; }
    }
    if (typeof raw.nutritionalInfo === 'string') {
      try { raw.nutritionalInfo = JSON.parse(raw.nutritionalInfo); } catch { raw.nutritionalInfo = null; }
    }
    if (raw.nutritionalInfo && !Array.isArray(raw.nutritionalInfo)) {
      const legacy = raw.nutritionalInfo;
      const arr: { label: string, value: string }[] = [];
      if (legacy.per) arr.push({ label: 'Per', value: legacy.per });
      if (legacy.calories != null) arr.push({ label: 'Calories', value: String(legacy.calories) });
      if (legacy.fat != null) arr.push({ label: 'Fat', value: String(legacy.fat) + 'g' });
      if (legacy.carbs != null) arr.push({ label: 'Carbs', value: String(legacy.carbs) + 'g' });
      if (legacy.protein != null) arr.push({ label: 'Protein', value: String(legacy.protein) + 'g' });
      if (legacy.sodium != null) arr.push({ label: 'Sodium', value: String(legacy.sodium) + 'mg' });
      raw.nutritionalInfo = arr;
    }
    
    // Fallback to defaults
    if (!Array.isArray(raw.ingredients)) raw.ingredients = [];
    if (!Array.isArray(raw.images)) raw.images = [];
    
    return raw;
  }
}
